/**
 * Medewerkersportaal — API-client (3 pijlers) + werkgeverkant.
 * Backend: src/routes/medewerker.ts en src/routes/medewerkerPortaalEmployer.ts
 */
import api from './api';
import type { User } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PoolStatus = 'GROEN' | 'BLAUW' | 'ORANJE' | 'ROOD';
export type AcademyCategorie = 'MENTAL' | 'PHYSICAL' | 'FINANCIAL' | 'GROWTH';
export type WkrLabel = 'gerichte_vrijstelling' | 'wkr_vrije_ruimte';
export type ThermometerPijlerKey = 'werkdruk_stress' | 'energie_cultuur' | 'fysieke_belasting' | 'retentie_werkgeluk';

export interface PortaalProfessional {
  professional_id: string;
  naam: string;
  professional_type: string;
  discipline_label: string;
  is_arbo: boolean;
  is_vertrouwenspersoon: boolean;
  bio: string | null;
  specialisaties: string[];
  intro_video_url: string | null;
  foto_url: string | null;
  alleen_online: boolean;
  pool_status: PoolStatus;
  sessies_gebruikt: number;
  sessies_max: number | null;
  plafond_bereikt: boolean;
  has_text_reports: boolean;
  lopend_traject: { soort: 'dossier' | 'vp'; id: string; booking_token: string | null } | null;
}

export interface PoolOverzicht {
  niveau: 'A' | 'B' | 'C' | null;
  arbo_module: boolean;
  professionals: PortaalProfessional[];
}

export type StartTrajectResultaat =
  | { soort: 'dossier'; dossier_id: string; booking_token: string }
  | { soort: 'vp'; melding_id: string };

export interface PotSaldo {
  totaal: number;
  gebruikt: number;
  resterend: number;
}

export interface Wallet {
  actief: boolean;
  model: 'budget' | 'on_demand' | 'cao_percentage' | 'payroll_bridge';
  boekjaar: number;
  budget_totaal: number | null;
  budget_gebruikt: number;
  budget_resterend: number | null;
  budget_koppeling_nodig: boolean;
  hobby_cap_bedrag: number | null;
  /** WKR-budgetgrendel: resterende hobby-/vrije ruimte (null = geen aparte grens). */
  hobby_resterend?: number | null;
  korting_pct: number;
  /** Payroll Bridge: twee fiscaal gescheiden potten (null bij de andere modellen). */
  potten?: { pro: PotSaldo; hobby: PotSaldo; status: 'ACTIVE' | 'INACTIVE' } | null;
}

export interface AcademySubcategorie {
  id: number;
  hoofdgroep: AcademyCategorie;
  naam: string;
  slug: string;
  emoji: string | null;
  omschrijving: string | null;
  volgorde: number;
  aantal: number;
}

/** Hoofdgroep ("map") met aantal trainingen en zijn subcategorieën (pills). */
export interface AcademyCategorieInfo {
  key: AcademyCategorie;
  label: string;
  emoji: string;
  omschrijving: string;
  voorbeelden: string;
  wkr_label: WkrLabel;
  aantal: number;
  subcategorieen: AcademySubcategorie[];
}

/** Thematische "Netflix"-rij voor grasduinen. */
export interface AcademyRij {
  key: string;
  emoji: string;
  titel: string;
  subtitel: string;
  trainingen: AcademyTraining[];
}

export interface AcademyCatalogus {
  hoofdgroepen: AcademyCategorieInfo[];
  subcategorieen: AcademySubcategorie[];
  rijen: AcademyRij[];
}

export interface AcademyTraining {
  id: number;
  titel: string;
  soort_opleiding: string | null;
  wanneer: string | null;
  locaties: string | null;
  prijs_excl_btw: string | null;
  duur: string | null;
  samenvatting: string | null;
  omslagfoto_url: string | null;
  academy_categorie: AcademyCategorie;
  wkr_label: WkrLabel | null;
  datum_tijd: string | null;
  locatie_detail: string | null;
  academy_subcategory_id: number | null;
  academy_tags: string[];
  subcategorie_naam: string | null;
  subcategorie_slug: string | null;
  subcategorie_emoji: string | null;
  organisatie_naam: string;
  provider_logo: string | null;
  bruto_bedrag: number;
  korting_bedrag: number;
  netto_bedrag: number;
  categorie_label: string;
  categorie_emoji: string;
  provider_id: number;
  leervorm: AcademyLeervorm;
  /** One-liner voor het hover-paneel (academy_pitch, anders eerste zin van de samenvatting). */
  pitch: string | null;
  /** Dynamic Demand Badge: top 10% meest geboekt (wekelijks berekend). */
  populair: boolean;
  certificaat: boolean;
  certificering: string | null;
  /** Alleen gevuld vanaf 5 reviews. */
  rating: { gemiddelde: number; aantal: number } | null;
  /** 4-cijferige postcode van de leslocatie (training, anders opleideradres) — voor de Reistijd-slider. */
  locatie_postcode: string | null;
  /** Belagom Expert-Aanbeveling: past binnen het advies-traject van de casemanager/bedrijfsarts. */
  geadviseerd: boolean;
  beschikbaarheid: {
    status: 'direct' | 'open' | 'plekken' | 'vol';
    heeft_sessies: boolean;
    eerstvolgende_start: string | null;
    vrije_plekken: number | null;
  };
}

export type AcademyLeervorm = 'e_learning' | 'workshop' | 'opleiding';

/** Belagom Expert-Aanbeveling (migratie 144): geadviseerde categorie/thema voor het herstel. */
export interface AcademyAdvies {
  id: string;
  dossier_id: string | null;
  hoofdgroep: AcademyCategorie;
  subcategorie_id: number | null;
  subcategorie_slug: string | null;
  subcategorie_naam: string | null;
  adviseur_rol: string | null;
  toelichting: string | null;
  actief: boolean;
  created_at: string;
}

export type AcademyShift = 'dag' | 'avond' | 'nacht';
export type AcademySortering = 'relevantie' | 'populair' | 'rating' | 'snelst';

/** Querystring van /medewerker/academy/trainingen (Discovery Engine). */
export interface AcademyTrainingenParams {
  hoofdgroep?: string | null;
  subcategorie?: string | null;
  zoek?: string | null;
  /** Komma-lijst: e_learning,workshop,opleiding */
  vorm?: string | null;
  /** Komma-lijst: dag,avond,nacht */
  shift?: string | null;
  /** Komma-lijst ISO-weekdagen 1–7 */
  dagen?: string | null;
  budget?: '1' | null;
  score?: '4.5' | '4.8' | null;
  wkr?: '1' | null;
  advies?: '1' | null;
  sort?: AcademySortering | null;
}

export interface AcademySuggesties {
  cursussen: Array<{ id: number; titel: string; emoji: string | null; organisatie_naam: string; soort_opleiding: string | null }>;
  opleiders: Array<{ id: number; naam: string; logo_url: string | null; aantal: number }>;
  themas: Array<{ hoofdgroep: AcademyCategorie; subcategorie_slug: string | null; naam: string | null; emoji: string | null }>;
}

export interface AcademySessieLes {
  volgnummer: number;
  start_at: string;
  eind_at: string;
  locatie: string | null;
}

/** Startmoment van een live workshop/opleiding (les 1 = startdatum). */
export interface AcademySessie {
  id: number;
  naam: string | null;
  vrij: number;
  status: 'AVAILABLE' | 'LOCKED_FULL' | 'EXPIRED';
  start_at: string | null;
  lessen: AcademySessieLes[];
}

export interface AcademyTrainingDetail extends AcademyTraining {
  leerdoelen: string | null;
  voor_wie: string | null;
  doelgroep: string | null;
  programma_onderdelen: string | null;
  niveau: string | null;
  accreditatie: string | null;
  docenten: string | null;
  studiebelasting: string | null;
  toelatingseisen: string | null;
  inclusief_materialen: boolean | null;
  lunch_inbegrepen: boolean | null;
  reviews: { aantal: number; balken: { relevantie: number; toepasbaarheid: number; expertise: number } | null };
  sessies: AcademySessie[];
  /** Medewerker staat op de wachtlijst voor een nieuwe startdatum. */
  op_wachtlijst: boolean;
}

export interface AcademyOpleiderProfiel {
  id: number;
  organisatie_naam: string;
  logo_url: string | null;
  bio: string | null;
  stad: string | null;
  website: string | null;
  header_url: string | null;
  certificeringen: string[];
  score: { gemiddelde: number; aantal: number; cursussen: number } | null;
  aantal_cursussen: number;
  trainingen: AcademyTraining[];
}

export interface AcademyBoeking {
  id: string;
  training_id: number;
  titel: string;
  wanneer: string | null;
  datum_tijd: string | null;
  locaties: string | null;
  academy_categorie: AcademyCategorie;
  categorie_label: string;
  wkr_label: WkrLabel;
  bruto_bedrag: number;
  korting_bedrag: number;
  netto_bedrag: number;
  wallet_deel: number;
  bijbetaling: number;
  status: 'wacht_op_betaling' | 'bevestigd' | 'geannuleerd';
  stripe_checkout_url: string | null;
  betaald_op: string | null;
  created_at: string;
  sessie_id: number | null;
  sessie_naam: string | null;
  sessie_start: string | null;
  aantal_lessen: number;
  heeft_agenda: boolean;
  /** 3-Assen Review: bevestigd en afgelopen (of on-demand) en nog niet beoordeeld. */
  kan_reviewen?: boolean;
  heeft_review?: boolean;
  /** Gezet bij annulering door de medewerker; bij < 48 u blijft status 'bevestigd' (annuleringsbescherming). */
  geannuleerd_op: string | null;
  annulering_kosteloos: boolean | null;
}

export interface BoekResultaat {
  boeking_id: string;
  status: string;
  bijbetaling: number;
  wallet_deel: number;
  netto_bedrag: number;
  checkout_url: string | null;
  sessie_id: number | null;
}

export interface ThermometerPijler {
  key: ThermometerPijlerKey;
  label: string;
  vraag: string;
  laag: string;
  hoog: string;
}

export interface ThermometerStatus {
  ronde_id: string;
  periode_label: string;
  type: 'kwartaal' | 'jaar';
  ingevuld: boolean;
  pijlers: ThermometerPijler[];
}

export interface MedewerkerAfspraak {
  id: string;
  start_tijd: string;
  eind_tijd: string;
  status: 'PENDING_APPROVAL' | 'TEGENVOORSTEL' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  locatie_type: 'ONLINE' | 'WERKGEVER' | 'EXTERN';
  locatie_omschrijving?: string | null;
  /** Bij een tegenvoorstel: het tijdstip dat de medewerker oorspronkelijk vroeg. */
  aangevraagd_start_tijd?: string | null;
  professional_naam: string;
  discipline_label: string;
}

/**
 * Poortwachter-document in de medewerker-cockpit (Digital Signing Wrapper):
 *   ter_ondertekening   wacht nog op de handtekening van de werkgever (alleen inzage)
 *   werkgever_getekend  🟠 te ondertekenen via SMS-2FA
 *   ondertekend         🟢 verzegeld; Audit-Certificaat beschikbaar
 */
export interface PoortwachterDocument {
  id: string;
  soort: string;
  soort_label: string;
  titel: string;
  inhoud: string;
  status: 'ter_ondertekening' | 'werkgever_getekend' | 'ondertekend';
  bron: 'professional' | 'werkgever_formulier' | 'werkgever_upload';
  heeft_bestand: boolean;
  bestand_naam: string | null;
  document_hash: string;
  zegel_hash: string | null;
  akkoordverklaring: string;
  telefoon_bekend: boolean;
  telefoon_suffix_bekend: string | null;
  vrijgegeven_op: string | null;
  werkgever_ondertekend_op: string | null;
  ondertekend_op: string | null;
  telefoon_suffix: string | null;
  professional_naam: string;
}

export interface MedewerkerDashboard {
  pseudoniem: string;
  bedrijfsnaam: string | null;
  professionals: { niveau: string | null; arbo_module: boolean; aantal: number; disciplines: number; lopend: number };
  academy: Wallet;
  thermometer: { periode_label: string; type: 'kwartaal' | 'jaar'; ingevuld: boolean };
  komende_afspraken: MedewerkerAfspraak[];
  te_ondertekenen: number;
}

export interface IntroSectie {
  emoji: string;
  titel: string;
  alineas: string[];
}

export interface MedewerkerIntro {
  bedrijfsnaam: string | null;
  secties: IntroSectie[];
  experts: IntroSectie & {
    rollen: Array<{ code: string; emoji: string; titel: string; tekst: string }>;
    overige_rollen: string[];
    slot: string;
  };
  hoe_werkt: IntroSectie & { onderdelen: Array<{ code: string; emoji: string; titel: string; tekst: string }> };
  noodhulp: string;
  afsluiting: string;
}

export interface VpSlots {
  professional_naam: string | null;
  sessie_type: 'intake' | 'followup';
  sessie_nummer: number;
  max_sessies: number;
  duur_minuten: number;
  slots: Array<{ start_tijd: string; eind_tijd: string }>;
}

// ─── Medewerker ───────────────────────────────────────────────────────────────

const d = <T,>(r: { data: { data: T } }) => r.data.data;

export const checkToegangscode = (code: string) =>
  api.get<{ data: { bedrijfsnaam: string | null; omschrijving: string | null } }>(`/medewerker/toegangscode/${encodeURIComponent(code)}`).then(d);

export const checkUitnodiging = (token: string) =>
  api.get<{ data: { voornaam: string | null; bedrijfsnaam: string | null } }>(`/medewerker/uitnodiging/${encodeURIComponent(token)}`).then(d);

export const registreerMedewerker = (body: { code?: string; uitnodigingToken?: string; email: string; password: string; phone?: string }) =>
  api.post<{ data: { user: User; token: string; pseudoniem: string; bedrijfsnaam: string | null } }>('/medewerker/registreren', body).then(d);

export const getDashboard = () => api.get<{ data: MedewerkerDashboard }>('/medewerker/dashboard').then(d);
export const getIntro = () => api.get<{ data: MedewerkerIntro }>('/medewerker/intro').then(d);
export const getProfessionals = () => api.get<{ data: PoolOverzicht }>('/medewerker/professionals').then(d);
/** Vrijwillige, anonieme opgave voor het jaarverslag (VTP/BMW); wordt los van het traject opgeslagen. */
export interface DemografieOpgave {
  geslacht: 'man' | 'vrouw' | 'non_binair' | null;
  leeftijdsband: 'lt25' | '25_40' | '41_55' | 'gt55' | null;
}

export const startTraject = (professionalId: string, demografie?: DemografieOpgave | null) =>
  api.post<{ data: StartTrajectResultaat }>(`/medewerker/professionals/${professionalId}/start`, demografie ? { demografie } : {}).then(d);
export const getAfspraken = () => api.get<{ data: MedewerkerAfspraak[] }>('/medewerker/afspraken').then(d);
/** Tegenvoorstel van de professional accepteren (akkoord) of weigeren. */
export const beantwoordTegenvoorstel = (afspraakId: string, akkoord: boolean) =>
  api.post<{ data: { afspraak_id: string; status: 'SCHEDULED' | 'CANCELLED' } }>(`/medewerker/afspraken/${afspraakId}/tegenvoorstel`, { akkoord }).then(d);

export const getVpSlots = (meldingId: string) => api.get<{ data: VpSlots }>(`/medewerker/vp/${meldingId}/slots`).then(d);
export const boekVp = (meldingId: string, body: { start_tijd: string; locatie_type: 'ONLINE' | 'WERKGEVER' | 'EXTERN' }) =>
  api.post<{ data: { start_tijd: string; eind_tijd: string; duur_minuten: number; sessie_type: string } }>(`/medewerker/vp/${meldingId}/boeken`, body).then(d);

export const getDocumenten = () => api.get<{ data: PoortwachterDocument[] }>('/medewerker/documenten').then(d);
/** Zonder telefoon gaat de OTP naar het al geverifieerde nummer van het account. */
export const vraagSmsCode = (documentId: string, telefoon?: string | null) =>
  api.post<{ data: { telefoon_suffix: string } }>(`/medewerker/documenten/${documentId}/sms-code`, { telefoon: telefoon || null }).then(d);
export const ondertekenDocument = (documentId: string, code: string) =>
  api.post<{ data: { ondertekend_op: string; hash: string; zegel_hash: string; document_hash: string } }>(`/medewerker/documenten/${documentId}/onderteken`, { code }).then(d);
export const documentBestandUrl = (documentId: string) => `${api.defaults.baseURL ?? ''}/medewerker/documenten/${documentId}/bestand`;
/** Belagom Audit-Certificaat (PDF) na de verzegeling; start direct de download. */
export const downloadCertificaat = async (doc: PoortwachterDocument) => {
  const r = await api.get(`/medewerker/documenten/${doc.id}/certificaat.pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Audit-Certificaat_${doc.soort}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

export const getAcademy = () =>
  api
    .get<{ data: { wallet: Wallet; catalogus: AcademyCatalogus; boekingen: AcademyBoeking[]; advies: AcademyAdvies | null } }>('/medewerker/academy')
    .then(d);
export const getAcademyTrainingen = (params: AcademyTrainingenParams) =>
  api.get<{ data: AcademyTraining[] }>('/medewerker/academy/trainingen', { params }).then(d);
export const getAcademySuggesties = (q: string, signal?: AbortSignal) =>
  api.get<{ data: AcademySuggesties }>('/medewerker/academy/suggesties', { params: { q }, signal }).then(d);
export const getAcademyTraining = (id: number) =>
  api.get<{ data: AcademyTrainingDetail }>(`/medewerker/academy/trainingen/${id}`).then(d);
export const getAcademyOpleider = (id: number) =>
  api.get<{ data: AcademyOpleiderProfiel }>(`/medewerker/academy/opleiders/${id}`).then(d);
/** Inschrijf-wrapper: gegevens voor het opleidingsinstituut. Nooit zichtbaar voor de werkgever. */
export interface AcademyInschrijving {
  voornaam: string;
  achternaam: string;
  /** ISO-datum YYYY-MM-DD */
  geboortedatum: string;
  prive_email: string;
  mobiel: string;
}
export const boekTraining = (trainingId: number, sessieId: number | null, inschrijving: AcademyInschrijving) =>
  api.post<{ data: BoekResultaat }>('/medewerker/academy/boeken', { training_id: trainingId, sessie_id: sessieId, inschrijving }).then(d);
/** Wachtlijst: seintje bij een nieuwe startdatum van een volgeboekte/datumloze cursus. */
export const zetOpWachtlijst = (trainingId: number) =>
  api.post<{ data: { aantal: number } }>(`/medewerker/academy/wachtlijst/${trainingId}`).then(d);
export const haalVanWachtlijst = (trainingId: number) =>
  api.delete(`/medewerker/academy/wachtlijst/${trainingId}`).then(() => undefined);
/** Annuleren met 48-uurs grendel: ≥ 48 u kosteloos, daarna annuleringsbescherming. */
export const annuleerAcademyBoeking = (boekingId: string) =>
  api.post<{ data: { kosteloos: boolean; status: string }; message: string }>(`/medewerker/academy/boekingen/${boekingId}/annuleren`).then((r) => r.data);
/** iCalendar-bestand met alle lesdata van een bevestigde boeking. */
export const downloadAcademyAgenda = async (boekingId: string, titel: string) => {
  const r = await api.get(`/medewerker/academy/boekingen/${boekingId}/agenda.ics`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `belagom-academy-${titel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) || 'agenda'}.ics`;
  a.click();
  URL.revokeObjectURL(url);
};
export const vraagTelefoonKoppelingSmsCode = (telefoon: string) =>
  api.post<{ data: { telefoon_suffix: string } }>('/medewerker/academy/telefoon/sms-code', { telefoon }).then(d);
export const bevestigTelefoonKoppeling = (code: string) =>
  api.post<{ data: { telefoon_hash: string } }>('/medewerker/academy/telefoon/bevestig', { code }).then(d);

export const getThermometer = () => api.get<{ data: ThermometerStatus }>('/medewerker/thermometer').then(d);
export const vulThermometerIn = (scores: Record<ThermometerPijlerKey, number>) =>
  api.post<{ data: { ronde_id: string; periode_label: string } }>('/medewerker/thermometer', scores).then(d);

// ─── Professional: Poortwachter-documenten ────────────────────────────────────

export interface PoortwachterDocumentProfessional {
  id: string;
  dossier_id: string;
  soort: string;
  titel: string;
  inhoud: string;
  status: 'concept' | 'ter_ondertekening' | 'werkgever_getekend' | 'ondertekend';
  vrijgegeven_op: string | null;
  ondertekend_op: string | null;
  ondertekening_hash: string | null;
  ondertekening_telefoon_suffix: string | null;
  created_at: string;
}

export const getPoortwachterDocumenten = (dossierId: string) =>
  api.get<{ data: { soorten: Record<string, string>; documenten: PoortwachterDocumentProfessional[] } }>(`/medewerker/professional/dossiers/${dossierId}/poortwachter`).then(d);
export const slaPoortwachterDocumentOp = (dossierId: string, body: { id?: string | null; soort: string; titel: string; inhoud: string; vrijgeven?: boolean }) =>
  api.post<{ data: PoortwachterDocumentProfessional }>(`/medewerker/professional/dossiers/${dossierId}/poortwachter`, body).then(d);

/** Belagom Expert-Aanbeveling: adviezen van de professional op een dossier (Academy advies-traject). */
export interface AcademyAdviesOverzicht {
  gekoppeld: boolean;
  adviezen: AcademyAdvies[];
  hoofdgroepen: Record<AcademyCategorie, { label: string; emoji: string; omschrijving: string }>;
  subcategorieen: AcademySubcategorie[];
}
export const getAcademyAdviezen = (dossierId: string) =>
  api.get<{ data: AcademyAdviesOverzicht }>(`/medewerker/professional/dossiers/${dossierId}/academy-advies`).then(d);
export const maakAcademyAdvies = (dossierId: string, body: { hoofdgroep: AcademyCategorie; subcategorie_id?: number | null; toelichting?: string | null }) =>
  api.post<{ data: AcademyAdvies }>(`/medewerker/professional/dossiers/${dossierId}/academy-advies`, body).then(d);
export const trekAcademyAdviesIn = (dossierId: string, adviesId: string) =>
  api.delete(`/medewerker/professional/dossiers/${dossierId}/academy-advies/${adviesId}`).then(() => undefined);

// ─── Werkgever (cockpit) ──────────────────────────────────────────────────────

export interface Toegangscode {
  id: string;
  code: string;
  omschrijving: string | null;
  location_id: string | null;
  department_id: string | null;
  vestiging_naam: string | null;
  afdeling_naam: string | null;
  max_gebruik: number | null;
  gebruikt: number;
  is_active: boolean;
  verloopt_op: string | null;
  created_at: string;
}

export interface AcademySettings {
  employer_id: string;
  model: 'budget' | 'on_demand' | 'cao_percentage' | 'payroll_bridge';
  budget_per_medewerker: number;
  cao_percentage: number | null;
  hobby_cap_bedrag: number | null;
  is_active: boolean;
  thermometer_frequentie: 'kwartaal' | 'jaar';
}

export interface CaoImportResultaat {
  geimporteerd: number;
  overgeslagen: number;
  fouten: string[];
}

export interface WkrOverzicht {
  boekjaar: number;
  aantal_medewerkers: number;
  budget_gereserveerd: number | null;
  per_wkr_label: Array<{ wkr_label: WkrLabel; aantal: number; wallet_totaal: number; korting_totaal: number }>;
  per_categorie: Array<{ categorie: AcademyCategorie; label: string; aantal: number; wallet_totaal: number }>;
}

export interface HittekaartGroep {
  soort: 'organisatie' | 'vestiging' | 'afdeling';
  id: string | null;
  naam: string;
  /** null = afgeschermd (onder de drempel, terug te rekenen, of ronde loopt nog) */
  aantal: number | null;
  onvoldoende_respons: boolean;
  scores: Record<ThermometerPijlerKey, number> | null;
}

export interface Hittekaart {
  ronde: { id: string; periode_label: string; type: string; start_op: string; eind_op: string };
  rondes: Array<{ id: string; periode_label: string; type: string; loopt: boolean }>;
  /** Resultaten pas na afloop van de ronde; alleen aantal_ingevuld is live. */
  ronde_loopt: boolean;
  aantal_ingevuld: number;
  aantal_medewerkers: number;
  minimum_groep: number;
  pijlers: ThermometerPijler[];
  groepen: HittekaartGroep[];
}

export interface UitnodigingenStatus {
  totaal: number;
  verzonden: number;
  geactiveerd: number;
  mislukt: number;
  percentage: number;
}

export interface UitnodigingenImportResultaat {
  geimporteerd: number;
  overgeslagen: number;
  fouten: string[];
}

export interface EmployerPortaalOverzicht {
  statistiek: {
    totaal: number;
    per_vestiging: Array<{ location_id: string | null; naam: string; aantal: number }>;
    per_afdeling: Array<{ department_id: string | null; naam: string; aantal: number }>;
  };
  toegangscodes: Toegangscode[];
  academy: AcademySettings;
  wkr: WkrOverzicht;
  categorieen: Record<AcademyCategorie, { label: string; omschrijving: string; voorbeelden: string; wkr_label: WkrLabel }>;
  uitnodigingen: UitnodigingenStatus;
  pool_contract_actief: boolean;
}

export const getEmployerPortaal = () => api.get<{ data: EmployerPortaalOverzicht }>('/employer/medewerkersportaal').then(d);
export const maakToegangscode = (body: { omschrijving?: string | null; location_id?: string | null; department_id?: string | null; max_gebruik?: number | null; verloopt_op?: string | null }) =>
  api.post<{ data: Toegangscode }>('/employer/medewerkersportaal/toegangscodes', body).then(d);
export const deactiveerToegangscode = (id: string) => api.delete(`/employer/medewerkersportaal/toegangscodes/${id}`);
export const updateAcademySettings = (body: Partial<Omit<AcademySettings, 'employer_id'>>) =>
  api.put<{ data: AcademySettings }>('/employer/medewerkersportaal/academy', body).then(d);
export const getWkrOverzicht = (jaar?: number) =>
  api.get<{ data: WkrOverzicht }>('/employer/medewerkersportaal/academy/wkr', { params: jaar ? { jaar } : {} }).then(d);
export const getHittekaart = (rondeId?: string | null) =>
  api.get<{ data: Hittekaart }>('/employer/medewerkersportaal/thermometer', { params: rondeId ? { ronde: rondeId } : {} }).then(d);
export const uploadCaoBudgetten = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api
    .post<{ data: CaoImportResultaat }>('/employer/medewerkersportaal/academy/cao-import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(d);
};

// ─── Werkgever: bulk-activatieketen ────────────────────────────────────────────

export const importUitnodigingen = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api
    .post<{ data: UitnodigingenImportResultaat }>('/employer/medewerkersportaal/uitnodigingen/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(d);
};
export const activeerUitnodigingen = () =>
  api.post<{ data: { verstuurd: number; mislukt: number } }>('/employer/medewerkersportaal/uitnodigingen/activeer').then(d);
export const getUitnodigingenStatus = () =>
  api.get<{ data: UitnodigingenStatus }>('/employer/medewerkersportaal/uitnodigingen/status').then(d);
